import { useState, useEffect, useCallback } from 'react';

const API_BASE_URL = 'https://glowgroove-2.onrender.com/api';

// 🛍️ Products Hook
export const useProducts = (filters) => {
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch product list
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();

      Object.entries(filters || {}).forEach(([key, value]) => {
        if (value !== '' && value !== undefined && value !== null) {
          queryParams.append(key, value);
        }
      });

      const res = await fetch(`${API_BASE_URL}/products?${queryParams}`);
      const data = await res.json();

      if (data.success) {
        setProducts(data.data.products);
        setPagination(data.data.pagination);
        setError(null);
      } else {
        setError(data.message || 'Failed to fetch products');
      }
    } catch (err) {
      console.error('Fetch products error:', err);
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // ✅ FIXED getProductById – no side effects
  const getProductById = useCallback(async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/products/${id}`);
      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.message || 'Failed to fetch product'
        };
      }

      return {
        success: true,
        data: data.data
      };
    } catch (err) {
      console.error('Error in getProductById:', err.message);
      return {
        success: false,
        message: 'Error connecting to server'
      };
    }
  }, []);

  return {
    products,
    loading,
    error,
    pagination,
    refetch: fetchProducts,
    getProductById
  };
};

// 🧭 Categories Hook
export const useCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/products/categories/list`);
        const data = await res.json();
        if (data.success) {
          setCategories(data.data.categories);
        } else {
          setError('Could not load categories');
        }
      } catch (err) {
        console.error('Fetch categories error:', err);
        setError('Error connecting to server');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  return { categories, loading, error };
};

// 🌟 Featured Products Hook
export const useFeaturedProducts = (limit = 6) => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/products/featured/list?limit=${limit}`);
        const data = await res.json();
        if (data.success) {
          setFeaturedProducts(data.data.products);
        } else {
          setError('Could not load featured products');
        }
      } catch (err) {
        console.error('Fetch featured products error:', err);
        setError('Error connecting to server');
      } finally {
        setLoading(false);
      }
    };

    fetchFeatured();
  }, [limit]);

  return { featuredProducts, loading, error };
};
